/**
 * Campaigns view — Phase 2.3.f.
 *
 * Owns the Campaigns tab: list (with search/threshold filter/pagination),
 * the campaign add/edit dialog (with products dropdown), the per-campaign
 * videos inline panel, and the video add/toggle/delete dialog.
 *
 * What stays in admin.js (injected via `configure({helpers})`):
 *   - `escapeHtml`, `openDialog`, `debounce` — generic.
 *   - `getViewTitle` — the topbar title element used to detect when the
 *     Campaigns view is active (the legacy code monkey-patched
 *     `switchView` to manage a topbar "Add campaign" button; we replicate
 *     that with the same approach via `getSwitchView`/`setSwitchView`).
 *
 * Phase 3.2.c: replaced `configure({helpers})` injection with direct
 * imports. The legacy `getSwitchView`/`setSwitchView` monkey-patch is
 * gone — admin.js's `switchView` now dispatches an `admin:viewchange`
 * custom event that this module listens to.
 */

import { escapeHtml, debounce } from '../../src/lib/format.js';
import { openDialog } from '../../src/lib/dialog.js';
import * as api from '../api-client.js';
import * as notify from '../notifications.js';

  const adminAPI = api;
  const API = api.routes;
  const showFlash = (msg, type, ms) => notify.showFlash(msg, type, ms);

  const getViewTitle = () => document.getElementById('viewTitle');

  // ---- DOM refs (lazy-resolved) ---------------------------------------
  let campaignsTbody, campaignDlg, campaignForm, campaignDlgTitle,
      campaignProductSelect, campaignVideosInline, campaignVideosTbody,
      addVideoBtn, videoDlg, videoForm, vSource, vUrl, vWeight, vVerify, vReq,
      campaignSearch, campaignAbove, campaignPrev, campaignNext, campaignPageInfo;
  let createCampaignBtn = null;

  let domResolved = false;
  function resolveDom() {
    if (domResolved) return;
    domResolved = true;
    campaignsTbody = document.getElementById('campaignsTbody');
    campaignDlg = document.getElementById('campaignDlg');
    campaignForm = document.getElementById('campaignForm');
    campaignDlgTitle = document.getElementById('campaignDlgTitle');
    campaignProductSelect = document.getElementById('cProductId');
    campaignVideosInline = document.getElementById('campaignVideosInline');
    campaignVideosTbody = document.getElementById('campaignVideosTbody');
    addVideoBtn = document.getElementById('addVideoBtn');
    videoDlg = document.getElementById('videoDlg');
    videoForm = document.getElementById('videoForm');
    vSource = document.getElementById('vSource');
    vUrl = document.getElementById('vUrl');
    vWeight = document.getElementById('vWeight');
    vVerify = document.getElementById('vVerify');
    vReq = document.getElementById('vReq');
    campaignSearch = document.getElementById('campaignSearch');
    campaignAbove = document.getElementById('campaignAboveThreshold');
    campaignPrev = document.getElementById('campaignPrev');
    campaignNext = document.getElementById('campaignNext');
    campaignPageInfo = document.getElementById('campaignPageInfo');
  }

  // ---- State ----------------------------------------------------------
  let currentCampaignIdForVideos = null;
  let campaignOffset = 0;
  const campaignLimit = 20;
  let campaignProductsLoaded = false;

  function ensureTopbarForView() {
    if (!createCampaignBtn) return;
    const viewTitle = getViewTitle();
    createCampaignBtn.style.display = (viewTitle && viewTitle.textContent === 'Campaigns') ? '' : 'none';
  }

  function renderCampaigns(items = [], total = 0) {
    campaignsTbody.innerHTML = '';
    if (!items.length) {
      campaignsTbody.innerHTML = '<tr><td colspan="11" style="color:var(--sub);">No campaigns</td></tr>';
      campaignPageInfo.textContent = '';
      return;
    }
    items.forEach((c, idx) => {
      const tr = document.createElement('tr');
      const avail = Number(c.available_balance || 0);
      const minth = (c.min_balance_threshold != null) ? Number(c.min_balance_threshold) : null;
      tr.innerHTML = `
        <td>${idx + 1 + campaignOffset}</td>
        <td>${escapeHtml(c.name || '')}</td>
        <td>${escapeHtml(String(c.product_id || ''))}</td>
        <td>${escapeHtml(c.corporate_customer_name || '')}</td>
        <td>${escapeHtml(c.status || '')}</td>
        <td>${avail.toFixed(2)}</td>
        <td>${minth != null ? minth.toFixed(2) : ''}</td>
        <td>${escapeHtml(String(c.per_device_limit || ''))}</td>
        <td>${escapeHtml(String(c.redemptions || 0))}</td>
        <td>${Number(c.active_reservations_total || 0).toFixed(2)}</td>
        <td>
          <button class="btn" data-act="refresh" data-id="${c.id}">🔄 Refresh</button>
          <button class="btn" data-act="edit" data-id="${c.id}">✏️ Edit</button>
          <button class="btn" data-act="videos" data-id="${c.id}">🎞 Videos</button>
          <button class="btn warn" data-act="complete" data-id="${c.id}">✅ Complete</button>
        </td>`;
      campaignsTbody.appendChild(tr);
    });
    const end = Math.min(total, campaignOffset + campaignLimit);
    campaignPageInfo.textContent = `${campaignOffset + 1}-${end} of ${total}`;
    campaignPrev.disabled = campaignOffset === 0;
    campaignNext.disabled = end >= total;
  }

  async function loadCampaignDetailForVideos(id) {
    try {
      const c = await adminAPI.fetch(API.adminCampaign(id), { method: 'GET' });
      renderCampaignVideos(c.videos || []);
      campaignVideosInline.style.display = '';
      addVideoBtn.onclick = () => { openVideoDialogForCampaign(id); };
    } catch (err) {
      showFlash(err.message, 'error');
    }
  }

  function renderCampaignVideos(videos = []) {
    campaignVideosTbody.innerHTML = '';
    if (!videos.length) {
      campaignVideosTbody.innerHTML = '<tr><td colspan="8" style="color:var(--sub);">No videos</td></tr>';
      return;
    }
    videos.forEach(v => {
      const tr = document.createElement('tr');
      tr.innerHTML = `
        <td>${v.id}</td>
        <td>${escapeHtml(v.source_kind)}</td>
        <td>${escapeHtml(v.url)}</td>
        <td>${escapeHtml(String(v.weight || ''))}</td>
        <td>${v.verification_enabled ? 'Yes' : 'No'}</td>
        <td>${v.acceptance_required_percent != null ? String(v.acceptance_required_percent) : ''}</td>
        <td>${v.active ? 'Yes' : 'No'}</td>
        <td>
          <button class="btn" data-vact="toggle" data-id="${v.id}">${v.active ? 'Disable' : 'Enable'}</button>
          <button class="btn danger" data-vact="delete" data-id="${v.id}">Delete</button>
        </td>`;
      campaignVideosTbody.appendChild(tr);
    });
  }

  function openVideoDialogForCampaign(id) {
    currentCampaignIdForVideos = id;
    videoForm.reset();
    vSource.value = 'cdn';
    vWeight.value = '1';
    vVerify.value = 'true';
    vReq.value = '95';
    openDialog(videoDlg);
  }

  async function openCampaignDialog(id = null) {
    campaignForm.reset();
    campaignForm.id.value = id || '';
    campaignDlgTitle.textContent = id ? 'Edit campaign' : 'Add campaign';
    await populateCampaignProducts();
    if (id) {
      try {
        const c = await adminAPI.fetch(API.adminCampaign(id), { method: 'GET' });
        campaignForm.name.value = c.name || '';
        campaignProductSelect.value = String(c.product_id || '');
        campaignForm.corporate_customer_name.value = c.corporate_customer_name || '';
        campaignForm.discount_percent.value = c.discount_percent != null ? c.discount_percent : 0;
        campaignForm.verification_markup_percent.value = c.verification_markup_percent != null ? c.verification_markup_percent : 0;
        campaignForm.per_device_limit.value = c.per_device_limit || 1;
        campaignForm.min_balance_threshold.value = c.min_balance_threshold != null ? c.min_balance_threshold : '';
      } catch (err) { showFlash(err.message, 'error'); return; }
    }
    openDialog(campaignDlg);
  }

  async function loadCampaigns() {
    const url = new URL(API.adminCampaigns, window.location.origin);
    url.searchParams.set('limit', String(campaignLimit));
    url.searchParams.set('offset', String(campaignOffset));
    const q = campaignSearch.value.trim();
    if (q) url.searchParams.set('search', q);
    if (campaignAbove.checked) url.searchParams.set('above_threshold', 'true');
    try {
      const data = await adminAPI.fetch(url.pathname + url.search, { method: 'GET' });
      const items = Array.isArray(data.items) ? data.items : [];
      renderCampaigns(items, Number(data.total || items.length));
    } catch (err) {
      renderCampaigns([]); showFlash(err.message, 'error');
    }
  }

  async function populateCampaignProducts() {
    if (campaignProductsLoaded && campaignProductSelect.options.length > 0) return;
    campaignProductSelect.innerHTML = '<option value="" disabled selected>Loading products…</option>';
    try {
      const products = await adminAPI.fetch(API.products, { method: 'GET' });
      const items = Array.isArray(products) ? products : (products.items || []);
      campaignProductSelect.innerHTML = '<option value="" disabled selected>Select a product…</option>';
      items.forEach(p => {
        const opt = document.createElement('option');
        opt.value = String(p.id);
        opt.textContent = `${p.id} — ${p.name}`;
        campaignProductSelect.appendChild(opt);
      });
      campaignProductsLoaded = true;
    } catch (err) {
      campaignProductSelect.innerHTML = '<option value="" disabled selected>Failed to load products</option>';
      showFlash(err.message, 'error');
    }
  }

  let mounted = false;
  function mount() {
    if (mounted) return;
    mounted = true;
    resolveDom();

    // Topbar "Add campaign" button (shown only when on Campaigns view).
    createCampaignBtn = document.createElement('button');
    createCampaignBtn.className = 'btn primary';
    createCampaignBtn.textContent = '➕ Add campaign';
    createCampaignBtn.style.display = 'none';
    const topbarRow = document.querySelector('.topbar .row');
    if (topbarRow) topbarRow.prepend(createCampaignBtn);
    createCampaignBtn.addEventListener('click', () => openCampaignDialog());

    // Phase 3.2.c: subscribe to admin.js's viewchange event instead of
    // monkey-patching switchView.
    document.addEventListener('admin:viewchange', ensureTopbarForView);

    if (campaignSearch) {
      campaignSearch.addEventListener('input', debounce(() => { campaignOffset = 0; loadCampaigns(); }, 300));
    }
    if (campaignAbove) campaignAbove.addEventListener('change', () => { campaignOffset = 0; loadCampaigns(); });
    if (campaignPrev) campaignPrev.addEventListener('click', () => { campaignOffset = Math.max(0, campaignOffset - campaignLimit); loadCampaigns(); });
    if (campaignNext) campaignNext.addEventListener('click', () => { campaignOffset += campaignLimit; loadCampaigns(); });

    if (campaignsTbody) {
      campaignsTbody.addEventListener('click', async (e) => {
        const btn = e.target.closest('button[data-act]');
        if (!btn) return;
        const id = btn.dataset.id;
        const act = btn.dataset.act;
        if (act === 'refresh') {
          btn.disabled = true; btn.textContent = 'Refreshing…';
          try { await adminAPI.fetch(API.adminCampaignRefresh(id), { method: 'POST' }); showFlash('Balance refreshed'); loadCampaigns(); }
          catch (err) { showFlash(err.message, 'error'); }
          finally { btn.disabled = false; btn.textContent = '🔄 Refresh'; }
        }
        if (act === 'complete') {
          if (!confirm('Mark this campaign as completed?')) return;
          try { await adminAPI.fetch(API.adminCampaignComplete(id), { method: 'POST' }); showFlash('Campaign completed'); loadCampaigns(); }
          catch (err) { showFlash(err.message, 'error'); }
        }
        if (act === 'edit') return openCampaignDialog(id);
        if (act === 'videos') {
          currentCampaignIdForVideos = id;
          await loadCampaignDetailForVideos(id);
        }
      });
    }

    if (campaignVideosTbody) {
      campaignVideosTbody.addEventListener('click', async (e) => {
        const btn = e.target.closest('button[data-vact]');
        if (!btn || !currentCampaignIdForVideos) return;
        const vid = btn.dataset.id;
        const act = btn.dataset.vact;
        if (act === 'toggle') {
          try {
            const row = btn.closest('tr');
            const isActive = row && row.children[6] && row.children[6].textContent === 'Yes';
            await adminAPI.fetch(API.adminCampaign(currentCampaignIdForVideos) + `/videos/${vid}`, { method: 'PATCH', body: JSON.stringify({ active: !isActive }) });
            showFlash('Video updated');
            await loadCampaignDetailForVideos(currentCampaignIdForVideos);
          } catch (err) { showFlash(err.message, 'error'); }
        }
        if (act === 'delete') {
          if (!confirm('Delete this video?')) return;
          try {
            await adminAPI.fetch(API.adminCampaign(currentCampaignIdForVideos) + `/videos/${vid}`, { method: 'DELETE' });
            showFlash('Video deleted');
            await loadCampaignDetailForVideos(currentCampaignIdForVideos);
          } catch (err) { showFlash(err.message, 'error'); }
        }
      });
    }

    if (videoForm) {
      videoForm.addEventListener('submit', async (e) => {
        e.preventDefault();
        if (!currentCampaignIdForVideos) return;
        const payload = {
          source_kind: vSource.value,
          url: vUrl.value.trim(),
          weight: Number(vWeight.value || 1),
          verification_enabled: vVerify.value === 'true',
          acceptance_required_percent: Number(vReq.value || 95)
        };
        try {
          await adminAPI.fetch(API.adminCampaign(currentCampaignIdForVideos) + '/videos', { method: 'POST', body: JSON.stringify(payload) });
          videoDlg.close();
          showFlash('Video added');
          await loadCampaignDetailForVideos(currentCampaignIdForVideos);
        } catch (err) { showFlash(err.message, 'error'); }
      });
    }

    if (campaignForm) {
      campaignForm.addEventListener('submit', async (e) => {
        e.preventDefault();
        const id = campaignForm.id.value.trim();
        const payload = {
          name: campaignForm.name.value.trim(),
          product_id: Number(campaignProductSelect.value),
          corporate_customer_name: campaignForm.corporate_customer_name.value.trim(),
          discount_percent: Number(campaignForm.discount_percent.value || 0),
          verification_markup_percent: Number(campaignForm.verification_markup_percent.value || 0),
          per_device_limit: Number(campaignForm.per_device_limit.value || 1),
          min_balance_threshold: campaignForm.min_balance_threshold.value === '' ? null : Number(campaignForm.min_balance_threshold.value)
        };
        try {
          if (id) {
            const patch = Object.fromEntries(Object.entries(payload).filter(([k, v]) => v !== null && v !== '' && !(typeof v === 'number' && isNaN(v))));
            await adminAPI.fetch(API.adminCampaign(id), { method: 'PATCH', body: JSON.stringify(patch) });
          } else {
            await adminAPI.fetch(API.adminCampaigns, { method: 'POST', body: JSON.stringify(payload) });
          }
          campaignDlg.close();
          showFlash('Campaign saved');
          loadCampaigns();
        } catch (err) { showFlash(err.message, 'error'); }
      });
    }
  }

export default {
  mount,
  init: () => { resolveDom(); loadCampaigns(); },
};
